export function byteToHexString(uint8arr) {
  if (!uint8arr) return '';
  var hexStr = '';
  for (var i = 0; i < uint8arr.length; i++) {
    var hex = (uint8arr[i] & 0xff).toString(16);
    hex = (hex.length === 1) ? '0' + hex : hex;
    hexStr += hex;
  }
  return hexStr.toUpperCase();
}

export function hexStringToByte(str) {
  if (!str) return new Uint8Array();
  var a = [];
  for (var i = 0, len = str.length; i < len; i += 2)
    a.push(parseInt(str.substr(i, 2), 16));
  return new Uint8Array(a);
}

export function hexStringToString(str) {
  let array = hexStringToByte(str);
  return new TextDecoder("utf-8").decode(array);
}

export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// CTAP2 communication over WebUSB
export async function ctapTransceive(device, command) {
  if (!device) throw new Error('Device not connected');

  // Ensure device is opened and configured
  if (!device.opened) {
    throw new Error('Device not opened');
  }

  // Find the FIDO interface (HID with usage page 0xF1D0)
  // Try to find and claim interface 0 for FIDO/CTAP operations if not already claimed
  let fidoInterface = null;
  for (const config of device.configurations) {
    for (const iface of config.interfaces) {
      // FIDO interface is typically interface 0 with class 3 (HID)
      if (iface.interfaceNumber === 0) {
        fidoInterface = iface;
        break;
      }
    }
    if (fidoInterface) break;
  }

  if (!fidoInterface) {
    throw new Error('FIDO interface not found');
  }

  // Check if interface 0 is already claimed, if not, claim it
  const claimedInterfaces = device.configuration?.interfaces.filter(i => i.claimed) || [];
  const interface0Claimed = claimedInterfaces.some(i => i.interfaceNumber === 0);

  if (!interface0Claimed) {
    try {
      await device.claimInterface(0);
    } catch (err) {
      throw new Error(`Failed to claim FIDO interface: ${err.message}`);
    }
  }

  const CTAPHID_CBOR = 0x10;
  const CTAPHID_INIT = 0x06;
  const CTAPHID_KEEPALIVE = 0x3B;
  const BROADCAST_CID = 0xFFFFFFFF;

  // Determine endpoint numbers from the interface
  const endpoint = fidoInterface.alternate.endpoints.find(ep => ep.direction === 'out');
  const endpointIn = fidoInterface.alternate.endpoints.find(ep => ep.direction === 'in');

  if (!endpoint || !endpointIn) {
    throw new Error('FIDO endpoints not found');
  }

  const endpointNumber = endpoint.endpointNumber;
  const endpointInNumber = endpointIn.endpointNumber;

  // Get or initialize channel ID
  let cid = device.ctapCid;
  if (!cid) {
    // Initialize CTAPHID channel
    const nonce = crypto.getRandomValues(new Uint8Array(8));
    const initCmd = new Uint8Array(64);
    const view = new DataView(initCmd.buffer);
    view.setUint32(0, BROADCAST_CID, false);
    initCmd[4] = 0x80 | CTAPHID_INIT;
    view.setUint16(5, 8, false);
    initCmd.set(nonce, 7);

    await device.transferOut(endpointNumber, initCmd);
    const response = await device.transferIn(endpointInNumber, 64);
    const respData = new Uint8Array(response.data.buffer);
    cid = new DataView(respData.buffer).getUint32(15, false);
    device.ctapCid = cid;
  }

  // Send CTAP command
  const packet = new Uint8Array(64);
  const view = new DataView(packet.buffer);
  view.setUint32(0, cid, false);
  packet[4] = 0x80 | CTAPHID_CBOR;
  view.setUint16(5, command.length, false);

  const payloadSize = Math.min(command.length, 57);
  packet.set(command.slice(0, payloadSize), 7);

  await device.transferOut(endpointNumber, packet);

  // Send continuation packets if needed
  let offset = payloadSize;
  let seq = 0;
  while (offset < command.length) {
    const contPacket = new Uint8Array(64);
    const contView = new DataView(contPacket.buffer);
    contView.setUint32(0, cid, false);
    contPacket[4] = seq++;
    const contSize = Math.min(command.length - offset, 59);
    contPacket.set(command.slice(offset, offset + contSize), 5);
    await device.transferOut(endpointNumber, contPacket);
    offset += contSize;
  }

  // Receive response
  let response = await device.transferIn(endpointInNumber, 64);
  let respData = new Uint8Array(response.data.buffer);

  // Handle keepalive
  while ((respData[4] & 0x7F) === CTAPHID_KEEPALIVE) {
    response = await device.transferIn(endpointInNumber, 64);
    respData = new Uint8Array(response.data.buffer);
  }

  const respView = new DataView(respData.buffer);
  const respLen = respView.getUint16(5, false);
  let result = new Uint8Array(respLen);
  const firstChunk = Math.min(respLen, 57);
  result.set(respData.slice(7, 7 + firstChunk), 0);

  // Receive continuation packets
  offset = firstChunk;
  seq = 0;
  while (offset < respLen) {
    response = await device.transferIn(endpointInNumber, 64);
    respData = new Uint8Array(response.data.buffer);
    const chunkSize = Math.min(respLen - offset, 59);
    result.set(respData.slice(5, 5 + chunkSize), offset);
    offset += chunkSize;
  }

  return result;
}

// Simple CBOR parser for CTAP responses
export function parseCBOR(data) {
  let offset = 0;
  
  function readByte() {
    return data[offset++];
  }
  
  function readBytes(n) {
    const result = data.slice(offset, offset + n);
    offset += n;
    return result;
  }
  
  function readUint(n) {
    let result = 0;
    for (let i = 0; i < n; i++) {
      result = (result << 8) | readByte();
    }
    return result;
  }
  
  function parseValue() {
    const byte = readByte();
    const majorType = byte >> 5;
    const additionalInfo = byte & 0x1F;
    
    let value;
    if (additionalInfo < 24) {
      value = additionalInfo;
    } else if (additionalInfo === 24) {
      value = readByte();
    } else if (additionalInfo === 25) {
      value = readUint(2);
    } else if (additionalInfo === 26) {
      value = readUint(4);
    } else if (additionalInfo === 27) {
      value = readUint(8);
    }
    
    switch (majorType) {
      case 0: // unsigned integer
        return value;
      case 1: // negative integer
        return -1 - value;
      case 2: // byte string
        return readBytes(value);
      case 3: // text string
        return new TextDecoder().decode(readBytes(value));
      case 4: // array
        const arr = [];
        for (let i = 0; i < value; i++) {
          arr.push(parseValue());
        }
        return arr;
      case 5: // map
        const map = {};
        for (let i = 0; i < value; i++) {
          const key = parseValue();
          const val = parseValue();
          map[key] = val;
        }
        return map;
      case 7: // special
        if (additionalInfo === 20) return false;
        if (additionalInfo === 21) return true;
        if (additionalInfo === 22) return null;
        if (additionalInfo === 23) return undefined;
        return null;
      default:
        return null;
    }
  }
  
  return parseValue();
}

// Simple CBOR encoder
export function encodeCBOR(value) {
  const result = [];
  
  function writeByte(b) {
    result.push(b);
  }
  
  function writeBytes(bytes) {
    result.push(...bytes);
  }
  
  function writeUint(value, n) {
    const bytes = [];
    for (let i = n - 1; i >= 0; i--) {
      bytes.push((value >> (i * 8)) & 0xFF);
    }
    writeBytes(bytes);
  }
  
  function encodeValue(val) {
    if (val === null) {
      writeByte(0xF6);
    } else if (val === undefined) {
      writeByte(0xF7);
    } else if (val === false) {
      writeByte(0xF4);
    } else if (val === true) {
      writeByte(0xF5);
    } else if (typeof val === 'number') {
      if (val >= 0) {
        if (val < 24) {
          writeByte(val);
        } else if (val < 256) {
          writeByte(0x18);
          writeByte(val);
        } else if (val < 65536) {
          writeByte(0x19);
          writeUint(val, 2);
        } else {
          writeByte(0x1A);
          writeUint(val, 4);
        }
      } else {
        const posVal = -1 - val;
        if (posVal < 24) {
          writeByte(0x20 | posVal);
        } else if (posVal < 256) {
          writeByte(0x38);
          writeByte(posVal);
        } else {
          writeByte(0x39);
          writeUint(posVal, 2);
        }
      }
    } else if (typeof val === 'string') {
      const bytes = new TextEncoder().encode(val);
      if (bytes.length < 24) {
        writeByte(0x60 | bytes.length);
      } else if (bytes.length < 256) {
        writeByte(0x78);
        writeByte(bytes.length);
      } else {
        writeByte(0x79);
        writeUint(bytes.length, 2);
      }
      writeBytes(bytes);
    } else if (val instanceof Uint8Array) {
      if (val.length < 24) {
        writeByte(0x40 | val.length);
      } else if (val.length < 256) {
        writeByte(0x58);
        writeByte(val.length);
      } else {
        writeByte(0x59);
        writeUint(val.length, 2);
      }
      writeBytes(val);
    } else if (Array.isArray(val)) {
      if (val.length < 24) {
        writeByte(0x80 | val.length);
      } else {
        writeByte(0x98);
        writeByte(val.length);
      }
      val.forEach(encodeValue);
    } else if (typeof val === 'object') {
      const keys = Object.keys(val);
      if (keys.length < 24) {
        writeByte(0xA0 | keys.length);
      } else {
        writeByte(0xB8);
        writeByte(keys.length);
      }
      keys.forEach(key => {
        encodeValue(parseInt(key));
        encodeValue(val[key]);
      });
    }
  }
  
  encodeValue(value);
  return new Uint8Array(result);
}
